import { ArrowLeft, Copy, Loader2, MapPin, TriangleAlert } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import type { State } from "@/api/util";
import type { Courier } from "@/types/courier";

import { useCourierOffices, useCouriers } from "@/api/courier-query";
import { useOrder, useUpdateOrderDropoff } from "@/api/order-query";
import { useStates } from "@/api/util";
import SearchDropdown from "@/components/dashboard/search-dropdown";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import useAuthStore from "@/store/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useRouter, useRouterState } from "@tanstack/react-router";

export const Route = createFileRoute("/vendor/contract/dropoff-location")({
	validateSearch: z.object({
		orderId: z.string()
	}),
	component: RouteComponent
});

const dropOffSchema = z.object({
	state: z.string({ required_error: "State is required" }).nonempty("State is required"),
	agent: z.string({ required_error: "Agent is required" }).min(1, "Agent is required").nonempty("Agent is required"),
	courierService: z.string({ required_error: "Courier service is required" }).nonempty("Courier service is required"),
	courierBranch: z.string({ required_error: "Courier branch is required" }).nonempty("Courier branch is required"),
	default: z.boolean()
});
function renderSelectItem(item: Courier) {
	return (
		<div className="flex items-center gap-3">
			<div className="p-2 bg-gray-100 rounded-lg">
				<MapPin size={20} />
			</div>
			<span className="capitalize">{item.name.toLowerCase()}</span>
		</div>
	);
}

function RouteComponent() {
	const router = useRouter();
	const { orderId } = Route.useSearch();

	const { state } = useRouterState({ select: state => state.location });

	const { data: order, isPending } = useOrder(orderId, state.order);
	const updateDropOff = useUpdateOrderDropoff();

	const courierQuery = useCouriers();
	const user = useAuthStore(state => state.user);
	const statesQuery = useStates(user?.countryId ?? "");

	const form = useForm<z.infer<typeof dropOffSchema>>({
		resolver: zodResolver(dropOffSchema),
		defaultValues: {
			state: order?.pickupLocation.stateId,
			agent: "",
			courierService: order?.pickupCourier.id,
			courierBranch: "",
			default: false
		}
	});

	const courierOfficeQuery = useCourierOffices({
		stateId: form.watch("state"),
		courierId: form.watch("courierService")
	});
	const courierBranchId = Number(form.watch("courierBranch"));
	const currentCourierOffice = useMemo(() => {
		return courierOfficeQuery.data?.data.data.find(item => item.id === courierBranchId);
	}, [courierOfficeQuery.data, courierBranchId]);

	const handleSubmit = async () => {
		if (!currentCourierOffice)
			throw new Error("Courier office not found");
		if (!order)
			throw new Error("Order not found");
		await updateDropOff.mutateAsync({
			contractId: order.contract.id,
			courierOfficeId: currentCourierOffice.id
		});
	};

	useEffect(() => {
		const state = statesQuery.data?.find(item => item.name?.toLowerCase() === order?.contract.shipoutState.toLowerCase());

		if (!state)
			return;
		if (order) {
			form.setValue("state", state.id);
			form.setValue("courierService", order.pickupCourier.id);
		}
	}, [form, order, statesQuery.data]);

	useEffect(() => {
		if (form.getValues("courierBranch") || !currentCourierOffice?.courierAgents.length) {
			form.setValue("agent", "");
			form.setError("agent", {
				type: "manual",
				message: "No agents available in this location"
			});
		}
	}, [currentCourierOffice, form]);

	if (isPending || statesQuery.isPending || courierOfficeQuery.isPending) {
		return (
			<div className=" max-w-7xl ">
				<Button
					variant="secondary"
					className="relative border p-2 overflow-hidden gap-2 bg-white mb-2"
				>
					<ArrowLeft className="size-4" />
					Back
				</Button>
				<Card className="mb-6 shadow-none space-y-4 min-h-[80vh] min-w-[60vw] grid place-content-center">
					<div className="flex flex-col items-center justify-center gap-4">
						<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
						<p className="text-muted-foreground">Loading order details...</p>
					</div>
				</Card>
			</div>
		);
	}

	return (
		<section className="flex flex-col space-y-4">
			<div className="flex items-center gap-2">
				<Button
					variant="secondary"
					className="relative border p-2 overflow-hidden gap-2 bg-white"
					onClick={() => router.history.back()}
				>
					<ArrowLeft className="size-4" />
					Back
				</Button>
			</div>

			<section className={cn("min-h-[80vh] p-6 bg-gray-50 rounded-xl flex flex-col gap-6 h-full")}>
				<div className="w-full space-y-6">
					<p className="text-lg text-center">Choose a dropoff location nearest to you</p>
				</div>

				<Form {...form}>
					<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
						<FormField
							control={form.control}
							name="state"
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<SearchDropdown
											// diasbled
											field={field}
											data={statesQuery.data}
											isPending={statesQuery.isPending}
											isError={statesQuery.isError}
											placeholder="Select state"
											searchPlaceholder="Search state"
											renderItem={renderSelectItem}
											filterFunction={(item: State, query: string) => item.name.toLowerCase().includes(query.toLowerCase())}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="courierService"
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<SearchDropdown
											field={field}
											diasbled
											data={courierQuery.data?.data.items}
											isPending={courierQuery.isPending}
											isError={courierQuery.isError}
											placeholder="Select courier service"
											searchPlaceholder="Search courier service"
											renderItem={renderSelectItem}
											filterFunction={(item: Courier, query: string) => item.name.toLowerCase().includes(query.toLowerCase())}

										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="courierBranch"
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<SearchDropdown
											field={field}
											diasbled={!form.getValues("courierService")}
											data={courierOfficeQuery.data?.data.data}
											isPending={courierOfficeQuery.isPending}
											isError={courierOfficeQuery.isError}
											placeholder="Select courier branch"
											searchPlaceholder="Search courier branch"
											renderItem={renderSelectItem}
											filterFunction={(item: Courier, query: string) => item.name.toLowerCase().includes(query.toLowerCase())}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<div className="space-y-2">

							<p className="text-sm text-muted-foreground">{currentCourierOffice ? `${currentCourierOffice.name} ${currentCourierOffice.address}` : ""}</p>
							<div className="space-y-2">
								<p className="text-sm">Intasida agents available in this location</p>
								<FormField
									control={form.control}
									name="agent"
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<RadioGroup value={field.value} onValueChange={field.onChange}>
													<div className="space-y-2">
														{
															currentCourierOffice?.courierAgents.length
																? currentCourierOffice?.courierAgents.map(agent => (
																	<div
																		role="button"
																		key={agent.name}
																		className={cn("flex items-center justify-between p-4 rounded-lg border", {
																			"bg-primary/5 border-primary": field.value === agent.name
																		})}
																	>
																		<div className="flex items-center gap-4 w-full">
																			<RadioGroupItem value={agent.name} id={agent.name} />
																			<Label className="w-full flex items-center justify-between" htmlFor={agent.name}>
																				<span>{agent.name}</span>
																				<span
																					role="button"
																					className="flex items-center text-primary gap-2"
																					onClick={() => {
																						navigator.clipboard.writeText(agent.phoneNumber);
																						toast.success("Phone number copied to clipboard");
																					}}
																				>
																					<Copy size={16} />
																					{agent.phoneNumber}
																				</span>
																			</Label>
																		</div>
																	</div>
																))
																: currentCourierOffice
																	? (
																			<p className="text-center p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg" role="alert">
																				No agents available in this location
																			</p>
																		)
																	: null
														}
													</div>
												</RadioGroup>
											</FormControl>
											{/* <FormMessage /> */}
										</FormItem>
									)}
								/>
							</div>
						</div>
						<Dialog>
							<DialogTrigger asChild>
								<Button
									disabled={!form.formState.isValid}
									className="w-full h-14"
								>
									Continue
								</Button>
							</DialogTrigger>
							<DialogContent>
								<DialogHeader>
									<DialogTitle className="font-medium text-base">Confirm Details</DialogTitle>
								</DialogHeader>
								<DialogDescription className="sr-only">Confirm Drop off Location</DialogDescription>
								<div>
									<div className="text-center flex items-center justify-center gap-2 p-4 mb-4 text-sm text-primary bg-hover-secondary rounded-lg" role="alert">
										<TriangleAlert className="size-6 text-primary font-light" />
										<span>
											Kindly review the information below before proceeding
										</span>
									</div>
									<div>
										<p>
											State :
											{" "}
											{statesQuery.data?.find(item => item.id === form.getValues("state"))?.name}
										</p>
										<p>
											Courier Service :
											{" "}
											{currentCourierOffice?.name}
										</p>
										Intasida Agent :
										{" "}
										{form.getValues("agent")}
									</div>
								</div>
								<DialogFooter>
									<DialogTrigger asChild>
										<Button className="w-full" disabled={updateDropOff.isPending} onClick={form.handleSubmit(handleSubmit)}>
											{updateDropOff.isPending
												? (
														<>
															<Loader2 size={20} className="animate-spin" />
															&nbsp; Loading....
														</>
													)
												: (
														"Confirm"
													)}
										</Button>
									</DialogTrigger>
								</DialogFooter>
							</DialogContent>
						</Dialog>

					</form>
				</Form>
			</section>
		</section>
	);
}
